import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, X, Edit } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUploadField from "../../components/ImageUploadField";

/**
 * Generic admin CMS panel.
 * fields: array of { name, label, type ('text' | 'textarea' | 'list' | 'json' | 'checkbox' | 'select'), options? }
 */
export default function CMSPanel({ title, endpoint, fields, primary = "name", subtitle, defaults = {} }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(endpoint);
      setItems(res.data || []);
    } catch (e) { console.error(e); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`${endpoint}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold inline-flex items-center gap-2" data-testid={`cms-new-${title.toLowerCase()}-btn`}>
          <Plus size={16} /> New
        </button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-zinc-500">Nothing yet.</p>}
        {items.map((it) => (
          <div key={it.id} className="glass rounded-xl p-4 flex items-center gap-3" data-testid={`cms-item-${it.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{it[primary] || it.title || it.id}</p>
              <p className="text-xs text-zinc-500 truncate">{it.tagline || it.role || it.industry || it.country || ""}{!it.is_active && " · INACTIVE"}</p>
            </div>
            <button onClick={() => { setEditing(it); setShowForm(true); }} className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs flex items-center gap-1"><Edit size={12} /> Edit</button>
            <button onClick={() => remove(it.id)} className="px-3 py-2 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs flex items-center gap-1"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>

      {showForm && (
        <FormModal
          title={`${editing ? "Edit" : "New"} ${title}`}
          fields={fields}
          initial={editing || defaults}
          onClose={() => setShowForm(false)}
          onSave={async (payload) => {
            try {
              if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
              else await api.post(endpoint, payload);
              toast.success("Saved");
              setShowForm(false);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          }}
        />
      )}
    </div>
  );
}

function FormModal({ title, fields, initial, onClose, onSave }) {
  const buildState = () => {
    const s = {};
    fields.forEach((f) => {
      let v = initial[f.name];
      if (v === undefined) {
        if (f.type === "checkbox") v = !!f.default;
        else if (f.type === "list" || f.type === "json" || f.type === "images") v = [];
        else v = "";
      }
      if (f.type === "list" && Array.isArray(v)) v = v.join("\n");
      if (f.type === "json" && typeof v !== "string") v = JSON.stringify(v, null, 2);
      s[f.name] = v;
    });
    return s;
  };
  const [form, setForm] = useState(buildState);
  const [saving, setSaving] = useState(false);

  const onChange = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    for (const f of fields) {
      let v = form[f.name];
      if (f.type === "list") payload[f.name] = (v || "").split("\n").map((x) => x.trim()).filter(Boolean);
      else if (f.type === "json") {
        try { payload[f.name] = JSON.parse(v || "[]"); } catch { toast.error(`Invalid JSON in ${f.label}`); setSaving(false); return; }
      }
      else if (f.type === "checkbox") payload[f.name] = !!v;
      else if (f.type === "number") payload[f.name] = v === "" ? null : Number(v);
      else if (f.type === "image" || f.type === "images") payload[f.name] = v || (f.type === "images" ? [] : "");
      else payload[f.name] = v;
    }
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto" data-testid="cms-form-modal">
      <div className="relative w-full max-w-2xl glass rounded-3xl p-7 my-8">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10"><X size={18} /></button>
        <h3 className="text-2xl font-bold mb-5">{title}</h3>
        <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-2 no-scrollbar">
          {fields.map((f) => (
            <div key={f.name}>
              {f.type === "image" || f.type === "images" ? (
                <ImageUploadField
                  label={f.label}
                  ratio={f.ratio || "16:9 · max 8MB"}
                  help={f.help}
                  value={form[f.name]}
                  onChange={(v) => onChange(f.name, v)}
                  multiple={f.type === "images"}
                />
              ) : (
                <>
                  <label className="text-xs uppercase tracking-widest text-zinc-400">{f.label}</label>
                  {f.type === "textarea" || f.type === "list" || f.type === "json" ? (
                    <textarea
                      value={form[f.name] ?? ""}
                      onChange={(e) => onChange(f.name, e.target.value)}
                      rows={f.type === "json" ? 6 : 3}
                      placeholder={f.placeholder}
                      className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm font-mono"
                    />
                  ) : f.type === "select" ? (
                    <select value={form[f.name] ?? ""} onChange={(e) => onChange(f.name, e.target.value)} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm">
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <label className="flex items-center gap-2 mt-2 text-sm">
                      <input type="checkbox" checked={!!form[f.name]} onChange={(e) => onChange(f.name, e.target.checked)} /> {f.label}
                    </label>
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : "text"}
                      value={form[f.name] ?? ""}
                      onChange={(e) => onChange(f.name, e.target.value)}
                      required={f.required}
                      placeholder={f.placeholder}
                      className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm"
                    />
                  )}
                  {f.help && <p className="text-[11px] text-zinc-500 mt-1">{f.help}</p>}
                </>
              )}
            </div>
          ))}
          <button disabled={saving} type="submit" className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}
